import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Layers, Users, Inbox, BarChart3, Film, Receipt, HelpCircle, Settings, Plus,
  ChevronsLeft, ChevronsRight, RotateCcw, Bookmark, KeyRound, Copy, Check, Wallet,
} from "lucide-react";
import { useCampaigns, useCollections, useInbox, useBloggerInbox } from "@/shared/api/wb";
import { useDraftStore } from "@/shared/store/draft";
import { useRoleStore } from "@/shared/store/role";
import { api } from "@/shared/api/client";
import { ConfirmModal } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";

type Item = {
  to: string;
  label: string;
  icon: typeof Layers;
  badge?: string | number;
  matchPrefix?: string;
};

const COLLAPSE_KEY = "lk2-sidebar-collapsed";

export function Sidebar({ hidden }: { hidden?: boolean }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const role = useRoleStore((s) => s.role);
  const activeBloggerId = useRoleStore((s) => s.activeBloggerId);
  const { data: campaigns } = useCampaigns();
  const { data: collections } = useCollections();
  const { data: inbox } = useInbox();
  const { data: bloggerInbox } = useBloggerInbox(role === "blogger" ? activeBloggerId : null);
  const unreadChats = inbox?.unread_threads_count ?? 0;
  const newRequests = (bloggerInbox?.placements ?? []).filter((p) => p.status === "invited").length;
  const reset = useDraftStore((s) => s.reset);
  const [collapsed, setCollapsedState] = useState<boolean>(() => localStorage.getItem(COLLAPSE_KEY) === "1");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState<"preset" | "blank" | null>(null);
  const [respondentCode, setRespondentCode] = useState<string | null>(null);
  const [respondentBusy, setRespondentBusy] = useState(false);
  const [respondentCopied, setRespondentCopied] = useState(false);

  const setCollapsed = (v: boolean) => {
    setCollapsedState(v);
    localStorage.setItem(COLLAPSE_KEY, v ? "1" : "0");
  };

  const totalCreatives = campaigns?.reduce((s, c) => s + (c.creatives_count || 0), 0) ?? 0;

  const advertiserItems: Item[] = [
    { to: "/account/projects",      label: "Мои РК",           icon: Layers,   badge: campaigns?.length, matchPrefix: "/account/projects|/campaign/new|/campaign/[^/]+$" },
    { to: "/catalog",                label: "Каталог блогеров", icon: Users },
    { to: "/collections",            label: "Подборки",         icon: Bookmark, badge: collections?.length, matchPrefix: "/collections" },
    { to: "/creatives",              label: "Мои креативы",     icon: Film,     badge: totalCreatives > 0 ? totalCreatives : undefined },
    { to: "/inbox",                  label: "Лента чатов",      icon: Inbox,    badge: unreadChats > 0 ? unreadChats : undefined },
    { to: "/analytics",              label: "Аналитика",        icon: BarChart3 },
    { to: "/docs",                   label: "Документы · ОРД",  icon: Receipt },
    { to: "/billing",               label: "Финансы",           icon: Wallet },
  ];

  const bloggerItems: Item[] = [
    { to: "/blogger-cabinet/inbox",   label: "Входящие заявки", icon: Inbox,  badge: newRequests > 0 ? newRequests : undefined },
    { to: "/blogger-cabinet/balance", label: "Баланс и выплаты", icon: Wallet },
  ];

  const items = role === "blogger" ? bloggerItems : advertiserItems;

  async function onDemoResetConfirmed() {
    const mode = confirmReset!;
    setConfirmReset(null);
    try { await api(`/api/v1/demo/reset?mode=${mode}`, { method: "POST" }); } catch {/* tolerate */}
    reset();
    qc.invalidateQueries();
    setSettingsOpen(false);
    navigate("/account/projects");
  }

  async function onGenerateRespondentCode() {
    setRespondentBusy(true);
    setRespondentCopied(false);
    try {
      const r = await api<{ code: string }>("/api/v1/respondent/otp", { method: "POST" });
      setRespondentCode(r.code);
    } catch {
      setRespondentCode("error");
    } finally {
      setRespondentBusy(false);
    }
  }

  async function onCopyRespondentCode() {
    if (!respondentCode) return;
    try {
      await navigator.clipboard.writeText(respondentCode);
      setRespondentCopied(true);
      setTimeout(() => setRespondentCopied(false), 1500);
    } catch {/* tolerate */}
  }

  return (
    <aside className={cn(
      "flex-shrink-0 bg-[var(--bg-2)] border-r border-ink-12 flex flex-col gap-1.5 p-[14px_10px] overflow-hidden transition-[width,opacity] duration-300 ease-in-out relative",
      hidden   ? "w-0 opacity-0 border-r-0 pointer-events-none" :
      collapsed ? "w-[64px] items-center opacity-100" :
                  "w-[248px] opacity-100",
    )}>
      <button
        onClick={() => navigate(role === "blogger" ? "/blogger-cabinet/inbox" : "/")}
        className={cn("flex items-center gap-2.5 px-2.5 pt-1.5 pb-[14px] bg-transparent border-0 cursor-pointer text-left w-full",
          collapsed && "justify-center")}>
        {collapsed
          ? <div className="w-8 h-8 rounded-md bg-lime grid place-items-center text-[var(--fg-on-accent)] font-display font-black text-[14px]">W</div>
          : <div className="font-display font-black uppercase text-[15px] tracking-[-0.02em]">WOWBLOGGER</div>
        }
      </button>

      {role !== "blogger" && (
        <NavLink
          to="/campaign/new"
          title="Новая РК"
          className={cn(
            "inline-flex items-center gap-2 h-9 rounded-pill bg-lime text-[var(--fg-on-accent)] font-display font-semibold text-[13px] hover:[background:color-mix(in_srgb,var(--wb-lime)_88%,black)] transition-colors mb-1.5",
            collapsed ? "w-9 justify-center" : "px-3 justify-start",
          )}
        >
          <Plus size={14} strokeWidth={3} /> {!collapsed && "Новая РК"}
        </NavLink>
      )}

      {!collapsed && <SectionLabel>{role === "blogger" ? "Кабинет блогера" : "Работа"}</SectionLabel>}
      {items.map((it) => {
        const Icon = it.icon;
        const matchRe = it.matchPrefix ? new RegExp(`^(${it.matchPrefix})`) : null;
        const active = matchRe ? matchRe.test(pathname) : pathname === it.to;
        return (
          <button key={it.to} onClick={() => navigate(it.to)}
            title={collapsed ? it.label : undefined}
            className={cn(
              "flex items-center gap-2.5 rounded-md text-[13.5px] font-medium transition-colors text-left",
              collapsed ? "w-10 h-10 px-0 justify-center" : "px-2.5 py-2",
              active ? "bg-white text-ink shadow-sm" : "text-ink-60 hover:bg-ink-6 hover:text-ink",
            )}>
            <Icon size={16} className="flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1">{it.label}</span>
                {it.badge !== undefined && it.badge !== null && (
                  <span className="ml-auto bg-lime text-[var(--fg-on-accent)] text-[10px] font-bold font-mono px-1.5 py-px rounded-pill">{it.badge}</span>
                )}
              </>
            )}
          </button>
        );
      })}

      <div className="flex-1" />

      {!collapsed && <SectionLabel>Аккаунт</SectionLabel>}
      <SidebarItem
        icon={HelpCircle} collapsed={collapsed}
        active={pathname.startsWith("/help")}
        onClick={() => navigate("/help")}
      >Помощь</SidebarItem>
      <SidebarItem
        icon={Settings} collapsed={collapsed}
        onClick={() => setSettingsOpen((o) => !o)}
        active={settingsOpen}
      >Настройки</SidebarItem>

      {settingsOpen && !collapsed && (
        <div className="ml-2 mb-1 grid gap-1 fade-up">
          <div className="px-2.5 pt-1 pb-0.5 text-[10px] uppercase tracking-[0.08em] text-ink-40 font-display font-semibold">Демо</div>
          <button onClick={() => setConfirmReset("preset")}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[12.5px] font-medium text-ink-60 hover:bg-ink-6 hover:text-ink text-left">
            <RotateCcw size={14} /> Предзаполненный
          </button>
          <button onClick={() => setConfirmReset("blank")}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[12.5px] font-medium text-ink-60 hover:bg-ink-6 hover:text-ink text-left">
            <RotateCcw size={14} /> Новый кабинет
          </button>
          <button onClick={onGenerateRespondentCode} disabled={respondentBusy}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[12.5px] font-medium text-ink-60 hover:bg-ink-6 hover:text-ink text-left disabled:opacity-60">
            <KeyRound size={14} /> {respondentBusy ? "Генерируем…" : "Пароль для респондента"}
          </button>
          {respondentCode === "error" && (
            <div className="mx-2.5 mt-1 px-2.5 py-1.5 rounded-md bg-danger/10 text-danger text-[11.5px]">
              Не удалось сгенерировать код
            </div>
          )}
          {respondentCode && respondentCode !== "error" && (
            <div className="mx-2.5 mt-1 rounded-md border border-ink-12 bg-white p-2 flex items-center gap-2">
              <code className="flex-1 font-mono text-[13px] tracking-[0.08em] text-ink select-all">{respondentCode}</code>
              <button onClick={onCopyRespondentCode}
                title={respondentCopied ? "Скопировано" : "Скопировать"}
                className="w-7 h-7 grid place-items-center rounded-md text-ink-40 hover:text-ink hover:bg-ink-6">
                {respondentCopied ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          )}
          {respondentCode && respondentCode !== "error" && (
            <div className="px-2.5 text-[11px] text-ink-40 leading-snug">
              Действует 7 дней, можно использовать многократно. Передайте респонденту — введёт на экране входа.
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? "Развернуть" : "Свернуть"}
        className="mt-2 self-end w-8 h-8 grid place-items-center text-ink-40 hover:text-ink hover:bg-ink-6 rounded-md"
      >
        {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
      </button>

      {confirmReset && (
        <ConfirmModal
          title={confirmReset === "blank" ? "Новый кабинет" : "Сбросить демо"}
          message={
            confirmReset === "blank"
              ? "Удалить все кампании, подборки, чаты, документы и обнулить баланс?"
              : "Вернуть 6 демо-кампаний, чаты, документы и баланс 380 000 ₽?"
          }
          confirmLabel={confirmReset === "blank" ? "Удалить всё" : "Сбросить"}
          danger={confirmReset === "blank"}
          onConfirm={onDemoResetConfirmed}
          onCancel={() => setConfirmReset(null)}
        />
      )}
    </aside>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.08em] text-ink-40 font-semibold px-2.5 pt-3.5 pb-1.5">
      {children}
    </div>
  );
}

function SidebarItem({ icon: Icon, children, onClick, active, collapsed }: {
  icon: typeof Layers; children: React.ReactNode; onClick: () => void; active?: boolean; collapsed: boolean;
}) {
  return (
    <button onClick={onClick}
      title={collapsed ? String(children) : undefined}
      className={cn(
        "flex items-center gap-2.5 rounded-md text-[13.5px] font-medium transition-colors text-left text-ink-60 hover:bg-ink-6 hover:text-ink",
        collapsed ? "w-10 h-10 px-0 justify-center" : "px-2.5 py-2",
        active && "bg-ink-6 text-ink",
      )}>
      <Icon size={16} className="flex-shrink-0" />
      {!collapsed && <span>{children}</span>}
    </button>
  );
}
